/**
 * Presentation helpers. No trading maths here — the API sends final numbers.
 *
 * Formatting is pinned to en-US (dot decimal, comma thousands) so a trade
 * price reads the same for every operator regardless of their browser locale.
 */

const LOCALE = 'en-US';

export function num(value: string | number | null | undefined, digits = 2): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString(LOCALE, { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

export function money(
  value: string | number | null | undefined,
  currency: string | null | undefined = null,
): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(n)) return '—';
  const body = n.toLocaleString(LOCALE, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return currency ? `${body} ${currency}` : body;
}

export function signedMoney(
  value: string | number | null | undefined,
  currency: string | null | undefined = null,
): string {
  const n = typeof value === 'string' ? Number(value) : value ?? NaN;
  if (!Number.isFinite(n)) return '—';
  const sign = n > 0 ? '+' : '';
  return `${sign}${money(n, currency)}`;
}

export function pct(fraction: string | number | null | undefined, digits = 2): string {
  if (fraction === null || fraction === undefined || fraction === '') return '—';
  const n = typeof fraction === 'string' ? Number(fraction) : fraction;
  if (!Number.isFinite(n)) return '—';
  return `${(n * 100).toLocaleString(LOCALE, { maximumFractionDigits: digits })}%`;
}

export function dateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toISOString().replace('T', ' ').replace(/\.\d+Z$/, 'Z');
}

export function dateOnly(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toISOString().slice(0, 10);
}

export function ago(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const secs = (Date.now() - d.getTime()) / 1000;
  if (!Number.isFinite(secs)) return iso;
  const abs = Math.abs(secs);
  const units: [number, string][] = [
    [60, 's'],
    [3600, 'm'],
    [86400, 'h'],
    [2592000, 'd'],
    [Infinity, 'mo'],
  ];
  let value = abs;
  let unit = 's';
  let prev = 1;
  for (const [limit, label] of units) {
    if (abs < limit) {
      value = abs / prev;
      unit = label;
      break;
    }
    prev = limit;
  }
  const rounded = Math.round(value);
  return secs >= 0 ? `${rounded}${unit} ago` : `in ${rounded}${unit}`;
}

export function isoDaysAgo(days: number): string {
  return new Date(Date.now() - days * 86400_000).toISOString();
}

export function titleCase(value: string): string {
  return value
    .replace(/[_.]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
